
import React from 'react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Shield, AlertTriangle, Eye, Lock, UserCheck, MessageSquare, Globe, Bell } from 'lucide-react';

const SafetyTips = () => {
  const safetyCategories = [
    {
      title: "Before Meeting",
      icon: <Eye className="w-8 h-8 text-purple-600" />,
      tips: [
        "Research your date online and verify their identity through social media or video calls",
        "Share only basic information until you establish trust",
        "Be wary of users who avoid video calls or make excuses about meeting in person",
        "Be cautious of users who ask for financial assistance or share sob stories early on",
        "Trust your instincts - if something feels off, it probably is"
      ]
    },
    {
      title: "Setting Up Meetings",
      icon: <MessageSquare className="w-8 h-8 text-purple-600" />,
      tips: [
        "Arrange to meet in a public place with plenty of people around",
        "Tell a friend or family member where you're going and who you're meeting",
        "Set up a safety call or text check-in during the date",
        "Arrange your own transportation to and from the meeting place",
        "Consider scheduling a brief first meeting, like coffee, rather than a long dinner"
      ]
    },
    {
      title: "Online Security",
      icon: <Lock className="w-8 h-8 text-purple-600" />,
      tips: [
        "Use a strong, unique password for your dating profile",
        "Never share financial information or send money to someone you've met online",
        "Be cautious about sharing photos that could reveal your location or workplace",
        "Use the dating platform's messaging system until you establish trust",
        "Report suspicious behavior to the platform administrators"
      ]
    },
    {
      title: "Red Flags",
      icon: <AlertTriangle className="w-8 h-8 text-purple-600" />,
      tips: [
        "Inconsistent information or contradictory stories",
        "Reluctance to video chat or meet in person after establishing communication",
        "Asking for financial assistance or expensive gifts",
        "Pressuring you to move communication off the platform quickly",
        "Love bombing - excessive compliments and declarations of love very early on"
      ]
    },
    {
      title: "During Meetings",
      icon: <UserCheck className="w-8 h-8 text-purple-600" />,
      tips: [
        "Stay sober and alert during first meetings",
        "Never leave your food or drinks unattended",
        "Keep your phone charged and accessible",
        "Trust your instincts - if you feel uncomfortable, leave",
        "Have an exit strategy or excuse prepared if needed"
      ]
    },
    {
      title: "Ongoing Communication",
      icon: <Bell className="w-8 h-8 text-purple-600" />,
      tips: [
        "Take your time getting to know the person before sharing personal details",
        "Be wary of users who seem controlling or get angry when you're not available",
        "Watch for consistency in their stories and claims",
        "Be cautious of users who try to isolate you from friends and family",
        "Remember that healthy relationships develop gradually"
      ]
    }
  ];

  return (
    <Layout>
      <div className="container mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4 bg-pink-gradient text-gradient">Safety Tips</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Your safety is our top priority. Follow these guidelines to ensure a secure and positive experience on our platform.
          </p>
        </div>

        {/* Hero section */}
        <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-xl p-8 mb-12 flex flex-col md:flex-row items-center">
          <div className="md:w-2/3 md:pr-8">
            <h2 className="text-2xl font-bold mb-4 text-purple-900">Your Safety Matters</h2>
            <p className="text-gray-700 mb-6">
              While we work hard to ensure our platform is safe, meeting someone for the first time always requires caution. 
              These safety guidelines will help you have a positive experience while protecting your wellbeing.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button className="bg-pink-gradient hover:opacity-90">
                Report a Concern
              </Button>
              <Button variant="outline" className="border-purple-500 text-purple-700 hover:bg-purple-50">
                Contact Support
              </Button>
            </div>
          </div>
          <div className="md:w-1/3 mt-8 md:mt-0 flex justify-center">
            <Shield className="w-32 h-32 text-purple-500 opacity-80" />
          </div>
        </div>

        {/* Safety tips grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {safetyCategories.map((category, index) => (
            <Card key={index} className="hover:shadow-md transition-shadow">
              <CardHeader className="flex flex-row items-center gap-4">
                {category.icon}
                <CardTitle>{category.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="list-inside space-y-2">
                  {category.tips.map((tip, tipIndex) => (
                    <li key={tipIndex} className="flex items-start">
                      <span className="inline-block w-2 h-2 bg-pink-500 rounded-full mt-2 mr-2"></span>
                      <span className="text-gray-700">{tip}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Emergency resources */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold mb-6 text-center">Emergency Resources</h2>
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex flex-col md:flex-row items-center mb-4">
              <AlertTriangle className="w-8 h-8 text-red-500 mr-4" />
              <p className="text-red-800 font-medium">
                If you ever feel you are in immediate danger, please contact your local emergency services immediately.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div className="bg-white p-4 rounded shadow-sm">
                <h3 className="font-semibold mb-2">Emergency Services</h3>
                <p className="text-gray-700">Call 911 (US) or your local emergency number</p>
              </div>
              <div className="bg-white p-4 rounded shadow-sm">
                <h3 className="font-semibold mb-2">National Dating Helpline</h3>
                <p className="text-gray-700">1-800-123-4567 (Available 24/7)</p>
              </div>
              <div className="bg-white p-4 rounded shadow-sm">
                <h3 className="font-semibold mb-2">Support Email</h3>
                <p className="text-gray-700">safety@pinkparadise.com</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SafetyTips;
